import React, { useMemo } from 'react';
import { APITypes, MedicalDataTypes, Dynamo } from '../../../primary-record-types';
import { getIntl } from '../../../utils/intl';
import { FastField } from 'formik';
import { getDateFormatValidator, required } from '../../../utils/validators';
import ImagesField, { formValueImagesToResourceImageUpdates, resourceImagesToFormValueImages } from './ImagesField';
import { FormValueImage } from './ImagesField/types';
import { allergyStatusMessages, allergyTypeMessages, reactionTypeDescription } from '../../../utils/sharedMessages';
import { handleFormikSubmit } from '../../../utils/handleFormikSubmit';
import { isResourceActive } from '../../../utils/resourceUtils/isResourceActive';
import useRecordEvent from '../../../hooks/useRecordEvent';
import { CenteredFormRow, Form, SplitFormRow } from '../../v2BaseComponents/Form';
import { FormStack } from '../../v2BaseComponents/Stack';
import { FormikInputs } from '../../v2BaseComponents/Inputs';

const intl = getIntl('AllergysForm', {
    save: 'Save',
    allergy: 'Allergy',
    notes: 'Describe Reaction',
    startDate: 'When did this begin?',
    endDate: 'When did this end?',
    status: 'Status',
    allergyType: 'Allergy Type',
    selectStatus: 'Select Status',
    selectAllergyType: 'Select Allergy Type',
    describeReaction: 'Describe the reaction',
    reactionType: 'Reaction Type',
});

export type AllergyFormOwnProps = {
    isLoading: boolean;
    startDateTime?: string;
    defaultResource?: MedicalDataTypes.PrimaryRecordAllergy;
    defaultImages?: Dynamo.ResourceImage[];
    onSubmit: (data: APITypes.medicalDataService.UpdateResourceBody<MedicalDataTypes.PrimaryRecordAllergy>) => void;
    isDisabled: boolean;
    profileId: string;
    resourceId?: string;
    onCancel: () => void;
};

type FormValue = {
    resource: MedicalDataTypes.PrimaryRecordAllergy;
    images: FormValueImage[];
};

const reactionTypes: MedicalDataTypes.PrimaryRecordAllergyReactionType[] = ['allergic', 'intolerant', 'avoidance'];

const getDefaultFormValue = (startDateTime?: string): FormValue => ({
    resource: {
        id: '', //ignored
        version: '1',
        type: 'allergy',
        name: '',
        startDateTime: startDateTime || '',
        endDateTime: '',
        status: 'active',
        allergyType: null,
        reactionType: null,
        notes: '',
    },
    images: [],
});

export type AllergyFormProps = AllergyFormOwnProps;

const AllergyForm: React.FC<AllergyFormProps> = ({
    isLoading,
    onSubmit,
    isDisabled,
    defaultImages,
    defaultResource,
    startDateTime,
    profileId,
    resourceId,
    onCancel,
}) => {
    const initialValues = useMemo<FormValue>(
        () =>
            defaultResource && defaultImages
                ? {
                      resource: defaultResource,
                      images: resourceImagesToFormValueImages(defaultImages),
                  }
                : getDefaultFormValue(startDateTime),
        [defaultImages, defaultResource, startDateTime],
    );
    const recordEvent = useRecordEvent();

    return (
        <Form
            actionContainerSubmit
            initialValues={initialValues}
            onSubmit={(values, helpers) => {
                onSubmit({
                    resourceUpdate: {
                        ...values.resource,
                        startDateTime: values.resource.startDateTime || undefined,
                        endDateTime: isResourceActive(values.resource) ? undefined : values.resource.endDateTime,
                    },
                    imageUpdates: formValueImagesToResourceImageUpdates(values.images),
                });
                recordEvent(!!defaultResource ? 'editAllergy' : 'createAllergy');
                handleFormikSubmit(values, helpers);
            }}
            onCancel={onCancel}
            cancelButtonVariant="secondary"
        >
            {({ values, setFieldValue }) => {
                return (
                    <FormStack>
                        <SplitFormRow>
                            <FastField
                                component={FormikInputs.Input}
                                name="resource.name"
                                isRequired
                                title={intl('allergy')}
                                isDisabled={isDisabled || isLoading}
                                validate={required}
                            />
                            <FastField
                                name="resource.allergyType"
                                title={intl('allergyType')}
                                component={FormikInputs.Select}
                                selectOptions={(['food', 'drug', 'environment'] as const).map((_) => ({
                                    label: allergyTypeMessages(_),
                                    value: _,
                                }))}
                                placeholder={intl('selectAllergyType')}
                                allowEmpty
                            />
                        </SplitFormRow>
                        <SplitFormRow>
                            <FastField
                                name="resource.reactionType"
                                title={intl('reactionType')}
                                component={FormikInputs.Select}
                                selectOptions={reactionTypes.map((_) => ({
                                    label: reactionTypeDescription(_),
                                    value: _,
                                }))}
                                placeholder={intl('describeReaction')}
                                allowEmpty
                            />
                            <FastField
                                component={FormikInputs.TextArea}
                                name="resource.notes"
                                title={intl('notes')}
                                isDisabled={isDisabled || isLoading}
                            />
                        </SplitFormRow>
                        <SplitFormRow>
                            <FastField
                                name="resource.startDateTime"
                                title={intl('startDate')}
                                component={FormikInputs.DateTimePicker}
                                validate={getDateFormatValidator('MM/DD/YYYY')}
                            />
                            <FastField
                                name="resource.status"
                                title={intl('status')}
                                component={FormikInputs.Select}
                                selectOptions={(['active', 'inactive', 'chronic', 'ruled-out'] as const).map((_) => ({
                                    label: allergyStatusMessages(_),
                                    value: _,
                                }))}
                                placeholder={intl('selectStatus')}
                                validate={required}
                            />
                        </SplitFormRow>
                        {isResourceActive(values.resource) ? null : (
                            <SplitFormRow>
                                <FastField
                                    name="resource.endDateTime"
                                    title={intl('endDate')}
                                    component={FormikInputs.DateTimePicker}
                                    validate={getDateFormatValidator('MM/DD/YYYY')}
                                />
                            </SplitFormRow>
                        )}
                        <CenteredFormRow>
                            <ImagesField
                                isDisabled={isDisabled || isLoading}
                                values={values.images}
                                resourceId={resourceId}
                                profileId={profileId}
                                category="allergy"
                                setFieldValue={setFieldValue}
                            />
                        </CenteredFormRow>
                    </FormStack>
                );
            }}
        </Form>
    );
};

export default React.memo(AllergyForm);
